/**
 * App Component - Aplicação Principal
 * Integra todos os componentes: Layout, Graph, Investigation
 *
 * Features:
 * - Header com ação "Nova Investigação"
 * - Stats cards no topo
 * - Lista de investigações
 * - Visualização de grafo selecionado
 * - Sidebar de detalhes do nó
 * - Toast notifications
 * - Footer
 */

import { useState, useEffect } from 'react';
import { NodeSingular } from 'cytoscape';

// Layout
import { Header } from './components/layout/Header';
import { NodeDetailsSidebar } from './components/layout/Sidebar';
import { Footer } from './components/layout/Footer';

// Investigation
import { CreateInvestigation } from './components/investigation/CreateInvestigation';
import { InvestigationList } from './components/investigation/InvestigationList';
import { InvestigationStats } from './components/investigation/InvestigationStats';

// Graph
import { GraphCanvas } from './components/graph/GraphCanvas';
import { FloatingGraphControls } from './components/graph/GraphControls';
import { GraphLegend } from './components/graph/GraphLegend';
import { NodeDetails } from './components/graph/NodeDetails';

// UI
import { ToastContainer, Toast } from './components/ui/Toast';

// Hooks
import { useInvestigation } from './hooks/useInvestigation';
import { useToast } from './hooks/useToast';
import { useGraph } from './hooks/useGraph';

function App() {
  // States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInvestigation, setSelectedInvestigation] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<NodeSingular | null>(null);
  const [isNodeDetailsSidebarOpen, setIsNodeDetailsSidebarOpen] = useState(false);

  // Hooks
  const { toasts, showSuccess, showError, hideToast } = useToast();

  const {
    investigation,
    investigations,
    loading,
    fetchAllInvestigations,
    fetchInvestigation,
    createInvestigation,
    expandNode,
    deleteInvestigation,
  } = useInvestigation({
    onSuccess: (inv) => {
      if (inv.investigacao_id || inv.id) {
        showSuccess('Sucesso!', 'Operação concluída');
      }
    },
    onError: (err) => {
      showError('Erro', err.message || 'Ocorreu um erro');
    },
  });

  const {
    cyRef,
    fitGraph,
    resetZoom,
    centerNode,
    exportPNG,
  } = useGraph({
    onNodeClick: (node) => {
      setSelectedNode(node);
      setIsNodeDetailsSidebarOpen(true);
    },
  });

  // Carregar investigações ao montar
  useEffect(() => {
    fetchAllInvestigations();
  }, [fetchAllInvestigations]);

  // Handlers
  const handleCreateInvestigation = async (payload: any) => {
    const newInvestigation = await createInvestigation(payload);
    if (newInvestigation) {
      setSelectedInvestigation(newInvestigation);
      showSuccess('Investigação criada!', 'ID: ' + (newInvestigation.investigacao_id || newInvestigation.id));
    }
  };

  const handleSelectInvestigation = (inv: any) => {
    setSelectedInvestigation(inv);
    const id = inv.investigacao_id || inv.id;
    if (id) {
      fetchInvestigation(id);
    }
  };

  const handleExpandNode = async (nodeId: string) => {
    if (!selectedInvestigation) return;
    const id = selectedInvestigation.investigacao_id || selectedInvestigation.id;
    if (id) {
      await expandNode(id, nodeId);
      showSuccess('Nó expandido', 'Grafo atualizado');
    }
  };

  const handleDeleteInvestigation = async (id: string) => {
    await deleteInvestigation(id);
    showSuccess('Investigação deletada', '');
    if (selectedInvestigation && (selectedInvestigation.investigacao_id === id || selectedInvestigation.id === id)) {
      setSelectedInvestigation(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <Header onNewInvestigation={() => setIsCreateModalOpen(true)} />

      {/* Main Content */}
      <main className="flex-1 w-full px-4 py-6 sm:px-6 sm:py-8 lg:max-w-7xl lg:mx-auto">

        {/* Stats Cards */}
        <section className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Dashboard
          </h2>
          <InvestigationStats investigations={investigations} />
        </section>

        {/* Investigações */}
        <section className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Investigações
            </h2>
            <button
              onClick={() => fetchAllInvestigations()}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              disabled={loading}
            >
              Atualizar
            </button>
          </div>

          <InvestigationList
            investigations={investigations}
            loading={loading}
            onSelect={handleSelectInvestigation}
            onDelete={handleDeleteInvestigation}
          />
        </section>

        {/* Visualização do Grafo */}
        {selectedInvestigation && (
          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Grafo de Relacionamentos
            </h2>

            {/* Container do grafo com controles e legenda */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">

              {/* Legenda (topo - mobile, lateral - desktop) */}
              <div className="p-4 border-b border-gray-200 lg:hidden">
                <GraphLegend compact />
              </div>

              {/* Grid: Legenda (esquerda) + Grafo (centro) */}
              <div className="flex flex-col lg:flex-row">
                {/* Legenda Desktop */}
                <div className="hidden lg:block lg:w-64 border-r border-gray-200 p-4">
                  <GraphLegend />
                </div>

                {/* Canvas do Grafo */}
                <div className="flex-1 relative" style={{ minHeight: '500px', height: '70vh' }}>
                  <GraphCanvas
                    investigation={investigation || selectedInvestigation}
                    onNodeClick={(node) => {
                      setSelectedNode(node);
                      setIsNodeDetailsSidebarOpen(true);
                    }}
                    onNodeExpand={handleExpandNode}
                    onBackgroundClick={() => {
                      setSelectedNode(null);
                      setIsNodeDetailsSidebarOpen(false);
                    }}
                  />

                  {/* Controles flutuantes */}
                  <FloatingGraphControls
                    cyRef={cyRef}
                    onFit={fitGraph}
                    onResetZoom={resetZoom}
                    onExportPNG={exportPNG}
                    position="top-right"
                  />
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <Footer variant="utility" />

      {/* Modals e Sidebars */}

      {/* Modal: Criar Investigação */}
      <CreateInvestigation
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateInvestigation}
        loading={loading}
      />

      {/* Sidebar: Detalhes do Nó */}
      <NodeDetailsSidebar
        isOpen={isNodeDetailsSidebarOpen}
        onClose={() => setIsNodeDetailsSidebarOpen(false)}
        title="Detalhes do Nó"
      >
        <NodeDetails
          node={selectedNode}
          onExpand={handleExpandNode}
          onCenter={(nodeId) => {
            centerNode(nodeId);
            showSuccess('Nó centralizado', '');
          }}
        />
      </NodeDetailsSidebar>

      {/* Toast Notifications */}
      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            show={true}
            type={toast.type}
            title={toast.title}
            message={toast.message}
            onClose={() => hideToast(toast.id)}
            duration={toast.duration}
          />
        ))}
      </ToastContainer>
    </div>
  );
}

export default App;
